import { IHistoricTenant } from 'Modules/Boardwork/Tasks/Sublease/shared/models/Sublease'
import { IInsuranceCasesResultModel } from 'Modules/HousingAssociation/Insurance/shared/models/Cases'
import { IListModel, IPaginationModel } from 'shared/models/List'
import { IAddress, IUnitIdentificationFields } from 'shared/models/Unit'

import type { JSX } from 'react'

export interface IUnitFilter extends IPaginationModel {
  searchText: string | null
  sortcolumn: string
  sortorder: string
  clientId: string | null
  addressId: number | null
  extendedQuery?: IUnitQueryExtension | null
  includeProxyOwners?: boolean
  includeTenants?: boolean
  includeCoResidents?: boolean
}

export interface IUnitQueryExtension {
  propertyType: IUnitListExtensionProperties
  propertyTypeValue?: number
  name: string
  id: string
}

export enum IUnitListExtensionProperties {
  ownerSince = 'OwnerSince',
  objects = 'Objects',
  meters = 'Meters',
  unitNumberDeveloper = 'UnitNumberDeveloper',
  remainingJointDebtAmount = 'RemainingJointDebtAmount',
}

export enum UnitSortFieldEnum {
  rentedOut = 'RentedOut',
  ssbNo = 'SsbNumber',
  address = 'UnitAddress',
  sectionNo = 'ShareOrSectionNumber',
  none = 'None',
  propertyNumber = 'PropertyNumber',

  // optional columns
  ownerSince = 'OwnerSince',
  objects = 'Objects',
  meters = 'Meters',
  unitNumberDeveloper = 'UnitNumberDeveloper',
  remainingJointDebtAmount = 'RemainingJointDebtAmount',
}

export enum UnitSortOrderEnum {
  asc = 'Ascending',
  desc = 'Descending',
}

interface IUnitReponseModel<T> {
  data: T
  loading: boolean
  error: unknown
}

export type IUnitResponseModel = IListModel<IUnitModel>

export interface IUnitModel {
  unitNumberDeveloper: string
  id: string
  unitAddress: IAddress
  identificationFields: IUnitIdentificationFields
  owners: string[]
  isSubleased: boolean
  boardDetails?: IUnitNote
  proxyOwners?: string[]
  // Extended columns
  ownersDetails?: IUnitOwnersDetails[]
  proxyOwnersDetails?: IUnitOwnersDetails[]
  coResidents?: string[]
  coResidentsDetails?: IUnitOwnersDetails[]
  tenants?: string[]
  tenantsDetails?: IUnitOwnersDetails[]
  ownerSince?: Date
  objectInfo?: string[]
  meterInfo?: string[]
  remainingJointDebtAmount?: string
}

export interface IUnitOwnersDetails {
  firstName: string
  lastName: string
  id: string
}

export interface IClientAddress {
  address: string
  zip: string
  city: string
  addressId?: number
}

export interface IUnitStoreModel {
  unit: IUnitReponseModel<IUnit | null>
  jointCost: IUnitReponseModel<IJointCost[] | null>
  owners: IUnitReponseModel<IUnitOwner[] | null>
  cohabitants: IUnitReponseModel<IUnitCohabitant[] | null>
  tenants: IUnitReponseModel<IUnitTenant[] | null>
  objects: IUnitReponseModel<IUnitObject[]>
  meters: IUnitReponseModel<IUnitMeter[]>
  additionalInformation: IUnitReponseModel<IUnitAddtionalInformation[]>
  notes: IUnitNotes
  proxyOwner: IUnitReponseModel<IUnitProxyOwner>
  insurance: IUnitReponseModel<IInsuranceCasesResultModel | null>
  historicalOwners: IUnitReponseModel<IHistoricalOwnerModel[] | null>
  historicalTenants: IUnitReponseModel<IHistoricTenant[] | null>
}

export interface IUnit {
  unitNumberDeveloper: JSX.Element
  address: string
  area: string
  bnr: number
  buildingAddressIndex: number
  buildingId: number
  buildingIndex: number
  buildingNumber: number
  bra: number | null
  braB: number | null
  braE: number | null
  braI: number | null
  categoryDescription: string
  categoryIndex: number
  city: string
  clauses: string | null
  clientId: string
  clientNo: number
  completedDate: Date | null
  elevator: boolean
  firstUseDate: Date | null
  floor: string
  geoLocation: string | null
  gnr: number
  grossArea: number | null
  hba: string | null
  heatingType: string | null
  heatingTypeNumber: string | null
  houseBuildingCooperativeCode: string
  id: string
  livingArea: number
  netArea: number | null
  nla: number | null
  numberOfRooms: number
  originalNumberOfRooms: number
  parkingType: string | null
  parkingTypeDescription: string | null
  parkingSpotNumber: string | null
  primaryRoom: number
  propertyLeasingNumber: number | null
  propertyNumber: number | null
  propertyRegistrationIndex: number | null
  // Andelsnr
  propertySectionNo: string | null
  // Aksjeboenhetsnr
  propertySharedNo: string | null
  // Seksjonsnr
  sectionNo: number | null
  ssbNo: string | null
  subArea: string | null
  subAreaId: number | null
  taxValue: number | null
  unitIndex: number
  unitTypeName: string | null
  unitTypeNumber: number
  useArea: number | null
  zip: string | null
  countryCodeTaxDirectorate: string | null
}
export interface IJointCost {
  month: number
  year: number
  monthName: string
  sum: number | undefined
  expenses: IJointCostExpense[]
}

export interface IUnitOwners {
  data: IUnitOwner[] | null
  loading: boolean
  error: unknown
}

export interface IUnitTenant {
  id: number
  firstName: string
  lastName: string
  name: string
  phoneNumber: string
  email: string
  nameId: string
  birthDate: Date
  tenantFrom: Date
  tenantTo: Date
}
export interface IUnitOwner {
  id: string
  ownerFrom: Date
  ownerTo: Date
  birthDate: Date
  lastName: string
  firstName: string
  phoneNumber: string
  email: string
  address: IAddress
  hasCreatedLogin?: boolean
}

export interface IUnitCohabitant {
  id: string
  firstName: string
  lastName: string
  phoneNumber: string
  email: string
  nameId: string
  nameNo: number
  birthDate: string
  name: string
  type: string
  hasCreatedLogin: boolean
}

export interface IUpdateContactModel {
  phonenumber: string
  email: string
}

export interface IJointCostExpense {
  name: string
  sum: number | undefined
}

export interface IUnitMeter {
  id: number
  name: string
  type: string
}
export interface IUnitAddtionalInformation {
  id: string
  unitId: string
  text: string
  sortOrder: number
}

export interface IUnitObject {
  id: string
  name: string
  type: string
}

export interface IUnitNotes extends IUnitReponseModel<IUnitNote | null> {
  processing: boolean
  processingError: unknown
}

export interface IUnitNote {
  createByUser: string
  created: Date
  notes: string
  updated: Date
  updatedByUser: string
}

export interface IUnitProxyOwner {
  name: string
  phone: string
  email: string
  proxyOwnerSince: Date
}

export interface IUnitExportSelectionModel {
  groups: IUnitExcelGroups[]
  pin: string
}

export interface IUnitExcelGroups {
  id: string
  description: string
  fields: IUnitExcelField[]
}

export interface IUnitExcelField {
  id: string
  description: string
  selected: boolean
}

export interface IUnitExcelToggle {
  groupId: string
  fieldId: string
  selected: boolean
}
export interface IExportRequestModel {
  unitQuery: IUnitQueryModel
  exportDetails: IUnitExportSelectionModel
}

export interface IUnitQueryModel extends Omit<IUnitFilter, 'sortcolumn'> {
  // Had to replace because different casing on sortcolumn
  sortColumn: string
}

export interface IHistoricalOwnerModel {
  name: string
  from: Date
  to: Date
}
